import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Users, Plus, TrendingUp, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import CreatePlanetDialog from '../components/CreatePlanetDialog';
import { attractivePlanetCount } from '../lib/socialProof';

const CATEGORIES = ['All','Music','Gaming','Anime','Tech','Dating','Food','Space','Chill','Welcome'];

export default function Planets() {
  const [planets, setPlanets] = useState([]);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const [createOpen, setCreateOpen] = useState(false);

  const loadPlanets = () => api.get('/planets').then(r => setPlanets(r.data)).catch(() => {});
  useEffect(() => { loadPlanets(); }, []);

  const filtered = planets.filter(p => {
    const matchQ = !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.description || '').toLowerCase().includes(q.toLowerCase());
    const matchC = cat === 'All' || (p.tags || []).includes(cat);
    return matchQ && matchC;
  });

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl md:text-3xl">Discover Planets</h1>
          <p className="text-white/60 mt-1 text-sm">Find your tribe across thousands of communities.</p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          data-testid="planets-create-btn"
          className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 text-white border-0 rounded-full px-4 text-sm"
        >
          <Plus size={14} className="mr-1" /> Create
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
          <Input value={q} onChange={e => setQ(e.target.value)} className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40" placeholder="Search planets..." />
        </div>
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setCat(c)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${cat === c ? 'bg-white text-black' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>{c}</button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl p-5 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(123,47,247,0.18), rgba(0,198,255,0.14))' }}>
        <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full blur-3xl" style={{ background: '#7B2FF7', opacity: 0.4 }} />
        <div className="relative flex items-center justify-between gap-6 flex-wrap">
          <div>
            <Badge className="bg-amber-400/20 text-amber-300 border-amber-400/30"><Sparkles size={12} className="mr-1" />Featured</Badge>
            <h2 className="font-display font-bold text-xl mt-2">Cosmic Beats Live Festival</h2>
            <p className="text-white/70 text-xs mt-1">12 DJs spinning this Saturday. Free entry.</p>
          </div>
          <Link to="/app/planets/p6"><Button className="bg-white text-black hover:bg-white/90 rounded-full font-semibold text-sm">Join</Button></Link>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(p => (
          <Link to={`/app/planets/${p.id}`} key={p.id} className="group rounded-2xl overflow-hidden bg-[#161622] border border-white/10 hover:border-white/20 hover:-translate-y-0.5">
            <div className={`aspect-[5/3] bg-gradient-to-br ${p.color} relative`}>
              <div className="absolute inset-0 flex items-center justify-center text-6xl group-hover:scale-110 transition">{p.emoji}</div>
              <div className="absolute top-2 left-2 flex items-center gap-1 text-[10px] uppercase tracking-wider bg-black/40 backdrop-blur px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" /> {attractivePlanetCount(p.online, p.id).toLocaleString()} online
              </div>
              <div className="absolute top-2 right-2 text-[10px] uppercase bg-black/40 backdrop-blur px-2 py-0.5 rounded-full flex items-center gap-1"><TrendingUp size={9} /> Hot</div>
            </div>
            <div className="p-4">
              <div className="font-display font-semibold truncate">{p.name}</div>
              <div className="text-[11px] text-white/50 mt-0.5">by {p.creator}</div>
              <p className="text-xs text-white/60 mt-2 line-clamp-2">{p.description}</p>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-white/50 flex items-center gap-1"><Users size={12} /> {p.members?.toLocaleString()}</div>
                <div className="flex gap-1">
                  {(p.tags || []).slice(0,2).map(t => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/60">{t}</span>)}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
      {filtered.length === 0 && <div className="text-center py-12 text-white/40">No planets matched.</div>}

      <CreatePlanetDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={loadPlanets}
      />
    </div>
  );
}
